// src/FilingPreview.js
import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import pdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// ─── Signature Pad ─────────────────────────────────────────────────────────────
function SignaturePad({ onDone, onCancel }) {
  const canvasRef      = useRef(null);
  const lastPos        = useRef(null);
  const currentDataUrl = useRef(null);
  const [isDrawing,    setIsDrawing   ] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [mode,         setMode        ] = useState('draw');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1B2B4B';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    setHasSignature(false);
    currentDataUrl.current = null;
  }, [mode]);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) return {
      x: (e.touches[0].clientX - rect.left) * scaleX,
      y: (e.touches[0].clientY - rect.top) * scaleY,
    };
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    lastPos.current = getPos(e, canvasRef.current);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
    setHasSignature(true);
    currentDataUrl.current = canvas.toDataURL('image/png');
  };

  const stopDraw = () => setIsDrawing(false);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    currentDataUrl.current = null;
  };

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
        ctx.drawImage(
          img,
          (canvas.width - img.width * scale) / 2,
          (canvas.height - img.height * scale) / 2,
          img.width * scale,
          img.height * scale
        );
        setHasSignature(true);
        currentDataUrl.current = canvas.toDataURL('image/png');
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="fp-sigpad">
      <div className="fp-sigpad-tabs">
        <button
          className={`fp-sigpad-tab ${mode === 'draw' ? 'active' : ''}`}
          onClick={() => setMode('draw')}
          type="button"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Draw
        </button>
        <button
          className={`fp-sigpad-tab ${mode === 'upload' ? 'active' : ''}`}
          onClick={() => setMode('upload')}
          type="button"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Upload
        </button>
      </div>

      <div className="fp-sigpad-canvas-wrap">
        {mode === 'draw' ? (
          <>
            <canvas
              ref={canvasRef}
              width={460}
              height={130}
              className="fp-sigpad-canvas"
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={stopDraw}
              onMouseLeave={stopDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={stopDraw}
            />
            {!hasSignature && (
              <div className="fp-sigpad-placeholder">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                Sign here
              </div>
            )}
          </>
        ) : (
          <label className="fp-sigpad-upload">
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} />
            {hasSignature ? (
              <div className="fp-sigpad-upload-done">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <polyline points="20 6 9 17 4 12" stroke="#22c894" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
                <span>Image loaded</span>
              </div>
            ) : (
              <div className="fp-sigpad-upload-prompt">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Click to upload signature</span>
                <span className="fp-sigpad-upload-hint">PNG, JPG or SVG</span>
              </div>
            )}
          </label>
        )}
      </div>

      <div className="fp-sigpad-actions">
        <button className="fp-sigpad-clear" onClick={clearCanvas} type="button" disabled={!hasSignature}>
          Clear
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="fp-sigpad-cancel" onClick={onCancel} type="button">
            Cancel
          </button>
          <button
            className="fp-sigpad-apply"
            onClick={() => onDone(currentDataUrl.current)}
            type="button"
            disabled={!hasSignature}
          >
            Apply Signature
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── PDF Canvas Page ──────────────────────────────────────────────────────────
// FIX: Overlay fields were positioned against the wrong box because the
// container used display:inline-block + width:100%, so percentage coords were
// calculated before the canvas had its final rendered size.
//
// Solution: measure the canvas's actual pixel dimensions AFTER render with
// useLayoutEffect, store them in state, and position overlays using those
// exact pixel values instead of percentages. This guarantees fields always
// land on the right spot regardless of zoom, window size, or scale prop.
function PDFPage({ pdfPage, pageNum, scale, overlayFields, localData, onFieldChange, editMode }) {
  const canvasRef    = useRef(null);
  const containerRef = useRef(null);
  const renderTaskRef = useRef(null);

  // Actual rendered pixel size of the canvas element (CSS pixels, not canvas buffer pixels)
  const [canvasSize, setCanvasSize] = useState({ w: 0, h: 0 });
  const [rendered,   setRendered  ] = useState(false);

  useEffect(() => {
    if (!pdfPage || !canvasRef.current) return;
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel();
      renderTaskRef.current = null;
    }
    setRendered(false);
    setCanvasSize({ w: 0, h: 0 });

    const viewport = pdfPage.getViewport({ scale });
    const canvas   = canvasRef.current;
    const ctx      = canvas.getContext('2d');
    canvas.width   = viewport.width;
    canvas.height  = viewport.height;

    const task = pdfPage.render({ canvasContext: ctx, viewport });
    renderTaskRef.current = task;
    task.promise
      .then(() => {
        renderTaskRef.current = null;
        setRendered(true);
      })
      .catch((err) => {
        if (err?.name !== 'RenderingCancelledException') console.error('PDF render error:', err);
        renderTaskRef.current = null;
      });

    return () => {
      if (renderTaskRef.current) { renderTaskRef.current.cancel(); renderTaskRef.current = null; }
    };
  }, [pdfPage, scale]);

  // After render, measure the canvas's ACTUAL CSS pixel dimensions.
  // This runs synchronously after the DOM paints, so getBoundingClientRect is accurate.
  useLayoutEffect(() => {
    if (!rendered || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    setCanvasSize({ w: rect.width, h: rect.height });
  }, [rendered]);

  // Also re-measure on window resize so overlays stay correct if user resizes.
  useEffect(() => {
    if (!rendered) return;
    const handleResize = () => {
      if (!canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      setCanvasSize({ w: rect.width, h: rect.height });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [rendered]);

  const pageFields = overlayFields.filter(f => f.page === pageNum);

  return (
    // FIX: container must be position:relative and sized to exactly wrap the canvas.
    // Use display:block (not inline-block) to avoid sub-pixel gaps.
    <div
      ref={containerRef}
      style={{ position: 'relative', display: 'block', width: '100%', lineHeight: 0 }}
    >
      <canvas
        ref={canvasRef}
        style={{ display: 'block', width: '100%', borderRadius: 4 }}
      />

      {/* Only render overlays once we have real canvas dimensions */}
      {rendered && editMode && canvasSize.w > 0 && pageFields.map((field) => {
        // Convert fractional positions to absolute px based on actual canvas CSS size
        const left   = field.x * canvasSize.w;
        const top    = field.y * canvasSize.h;
        const width  = field.w * canvasSize.w;
        const height = field.h * canvasSize.h;

        return (
          <div
            key={field.key + field.x + field.y}
            style={{
              position: 'absolute',
              left,
              top,
              width,
              height,
              // Prevent overflow outside the page
              overflow: 'visible',
            }}
          >
            {/* Label above the field */}
            <span style={{
              position: 'absolute',
              top: -15,
              left: 0,
              fontSize: 8,
              color: '#3B7DD8',
              fontWeight: 700,
              background: 'white',
              padding: '1px 4px',
              borderRadius: 3,
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
              zIndex: 10,
            }}>
              {field.label}
            </span>

            <input
              type="text"
              value={localData[field.key] || ''}
              onChange={(e) => onFieldChange(field.key, e.target.value)}
              placeholder={field.placeholder || ''}
              style={{
                width: '100%',
                height: '100%',
                border: '1.5px solid #3B7DD8',
                borderRadius: 3,
                background: 'rgba(219, 234, 254, 0.92)',
                // Font size scaled to canvas — field.fontSize is relative to 1x scale
                fontSize: Math.max(7, (field.fontSize || 9) * (canvasSize.w / 612)),
                padding: '0 4px',
                outline: 'none',
                boxSizing: 'border-box',
                fontFamily: 'Helvetica, Arial, sans-serif',
                color: '#000000',
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

// ─── Overlay field definitions ─────────────────────────────────────────────────
// Coordinates are fractions of page width/height (0.0–1.0).
// Page 1 = Form 1120, Page 2 = Form 5472 page 1, Page 3 = Form 5472 page 2.
// IMPORTANT: These coordinates must match where the fields actually render in
// the flattened PDF. If they drift after a backend change, adjust x/y/w/h here.
function buildOverlayFields() {
  return [
    // ── Form 1120 (page 1) ──────────────────────────────────────────────────
    { page: 1, key: 'llcName',          label: 'LLC Name',             x: 0.20, y: 0.105, w: 0.36, h: 0.022, fontSize: 9 },
    { page: 1, key: 'ein',              label: 'EIN',                  x: 0.72, y: 0.105, w: 0.22, h: 0.022, fontSize: 9 },
    { page: 1, key: 'ownerAddress',     label: 'Street',               x: 0.20, y: 0.131, w: 0.36, h: 0.022, fontSize: 9 },
    { page: 1, key: 'city',             label: 'City',                 x: 0.20, y: 0.157, w: 0.16, h: 0.022, fontSize: 9 },
    { page: 1, key: 'stateCode',        label: 'State/Province',       x: 0.38, y: 0.157, w: 0.10, h: 0.022, fontSize: 9 },
    { page: 1, key: 'country',          label: 'Country',              x: 0.50, y: 0.157, w: 0.15, h: 0.022, fontSize: 9 },

    // ── Form 5472 page 1 (page 2) ───────────────────────────────────────────
    { page: 2, key: 'llcName',          label: 'LLC Name (1d)',        x: 0.07, y: 0.160, w: 0.50, h: 0.028, fontSize: 9 },
    { page: 2, key: 'ein',              label: 'EIN (1b)',             x: 0.60, y: 0.160, w: 0.28, h: 0.028, fontSize: 9 },
    { page: 2, key: 'ownerAddress',     label: 'Street',               x: 0.07, y: 0.192, w: 0.50, h: 0.025, fontSize: 8 },
    { page: 2, key: 'businessActivity', label: 'Business Activity (1a)', x: 0.07, y: 0.245, w: 0.42, h: 0.025, fontSize: 8 },
    { page: 2, key: 'businessCode',     label: 'NAICS Code (1e)',      x: 0.52, y: 0.245, w: 0.16, h: 0.025, fontSize: 8 },
    { page: 2, key: 'ownerName',        label: 'Owner Name (4a)',      x: 0.07, y: 0.510, w: 0.40, h: 0.025, fontSize: 9 },
    { page: 2, key: 'ownerAddress',     label: 'Owner Address',        x: 0.07, y: 0.538, w: 0.40, h: 0.025, fontSize: 8 },
    { page: 2, key: 'ownerCountry',     label: 'Country (4d)',         x: 0.26, y: 0.582, w: 0.20, h: 0.025, fontSize: 8 },

    // ── Form 5472 page 2 (page 3) ───────────────────────────────────────────
    { page: 3, key: 'ownerName',        label: 'Related Party (8a)',   x: 0.07, y: 0.104, w: 0.40, h: 0.025, fontSize: 9 },
    { page: 3, key: 'ownerCountry',     label: 'Country (8f)',         x: 0.07, y: 0.200, w: 0.20, h: 0.025, fontSize: 8 },
  ];
}

// ─── Step indicator ───────────────────────────────────────────────────────────
function StepBadge({ num, label, done, active }) {
  return (
    <div className={`fp-step ${done ? 'done' : ''} ${active ? 'active' : ''}`}>
      <div className="fp-step-circle">
        {done ? (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <polyline points="20 6 9 17 4 12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (
          <span>{num}</span>
        )}
      </div>
      <span className="fp-step-label">{label}</span>
    </div>
  );
}

// ─── Main FilingPreview component ─────────────────────────────────────────────
// Default summary fields — the original hardcoded Entity/Tax Year/EIN/Owner
// set for Form 5472. Other tools (EIN) pass their own `summaryFields`.
const DEFAULT_SUMMARY_FIELDS = [
  { label: 'Entity',    key: 'llcName'   },
  { label: 'Tax Year',  key: 'taxYear'   },
  { label: 'EIN',       key: 'ein'       },
  { label: 'Owner',     key: 'ownerName' },
];

export function FilingPreview({
  filingData,
  transactions: initialTransactions,
  setTransactions,
  generatedPdfBase64,
  isProcessing,
  processingError,
  onSendToIRS,
  onBack,
  onDownload,
  onRegeneratePDF,
  // ── Config props — all optional, all default to exact current 5472 behavior ──
  title,                                          // doc name in top bar
  docBadge = 'IRS Filing',                        // badge next to doc name
  overlayFields: overlayFieldsProp,                // inline-edit fields on the PDF; [] hides Edit Fields entirely
  showTxSummary = true,                           // Paid to LLC / From LLC chips
  summaryFields = DEFAULT_SUMMARY_FIELDS,          // Filing Summary grid
  sendLabel = 'Send to IRS via Fax',
  sendingLabel = 'Sending to IRS…',
  footerNote = 'Faxed directly to IRS · Delivery confirmed',
  confirmText = 'I confirm this filing is accurate and authorise submission to the IRS',
}) {
  const [localData,         setLocalData        ] = useState({ ...filingData });
  const [localTransactions, setLocalTransactions] = useState(initialTransactions || []);
  const [signatureData,     setSignatureData    ] = useState(filingData?.signatureBase64 || null);
  const [showSigPad,        setShowSigPad       ] = useState(false);
  const [confirmed,         setConfirmed        ] = useState(false);
  const [isRegenerating,    setIsRegenerating   ] = useState(false);
  const [pdfPages,          setPdfPages         ] = useState([]);
  const [isDirty,           setIsDirty          ] = useState(false);
  const [editMode,          setEditMode         ] = useState(false);

  const SCALE = 1.8;

  useEffect(() => {
    if (!generatedPdfBase64) return;
    const loadPDF = async () => {
      const binary = atob(generatedPdfBase64);
      const bytes  = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const pdf   = await pdfjsLib.getDocument({ data: bytes }).promise;
      const pages = [];
      for (let i = 1; i <= pdf.numPages; i++) pages.push(await pdf.getPage(i));
      setPdfPages(pages);
    };
    loadPDF();
  }, [generatedPdfBase64]);

  const handleFieldChange = useCallback((key, value) => {
    setLocalData(prev => ({ ...prev, [key]: value }));
    setIsDirty(true);
    setConfirmed(false);
  }, []);

  const handleRegenerate = async () => {
    if (!onRegeneratePDF) return;
    setIsRegenerating(true);
    try {
      const dataWithSig = { ...localData, signatureBase64: signatureData };
      await onRegeneratePDF(localTransactions, dataWithSig);
      setIsDirty(false);
      setEditMode(false);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleExitEditMode = () => {
    if (isDirty) {
      if (window.confirm('You have unsaved edits. Discard changes and exit editing?')) {
        setLocalData({ ...filingData });
        setIsDirty(false);
        setEditMode(false);
      }
    } else {
      setEditMode(false);
    }
  };

  const handleSignatureDone = (dataUrl) => {
    setSignatureData(dataUrl);
    setShowSigPad(false);
    setConfirmed(false);
  };

  const handleSend = () => {
    const dataWithSig = { ...localData, signatureBase64: signatureData };
    setTransactions(localTransactions);
    onSendToIRS(dataWithSig, localTransactions);
  };

  const overlayFields = overlayFieldsProp || buildOverlayFields();

  const fmt = (n) => (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const totalPaid     = localTransactions.filter(t => t.direction === 'paid_to_llc').reduce((s, t) => s + (t.amount || 0), 0);
  const totalReceived = localTransactions.filter(t => t.direction === 'received_from_llc').reduce((s, t) => s + (t.amount || 0), 0);

  const step1Done = !!generatedPdfBase64 && !isDirty;
  const step2Done = !!signatureData;
  const step3Done = confirmed;

  return (
    <div className="fp-root">

      {/* ── TOP BAR ─────────────────────────────────────────────────── */}
      <div className="fp-topbar">
        <div className="fp-topbar-left">
          <button className="fp-back-btn" onClick={onBack} type="button">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path d="M19 12H5M5 12l7 7M5 12l7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back
          </button>
          <div className="fp-topbar-divider" />
          <div className="fp-doc-info">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="fp-doc-name">
              {title || `Form 5472 — ${filingData?.llcName} — ${filingData?.taxYear}`}
            </span>
            <span className="fp-doc-badge">{docBadge}</span>
          </div>
        </div>

        <div className="fp-topbar-steps">
          <StepBadge num="1" label="Review" done={step1Done} active={!step1Done} />
          <div className="fp-step-connector" />
          <StepBadge num="2" label="Sign" done={step2Done} active={step1Done && !step2Done} />
          <div className="fp-step-connector" />
          <StepBadge num="3" label="Submit" done={step3Done} active={step2Done && !step3Done} />
        </div>

        <div className="fp-topbar-right">
          <button className="fp-btn-ghost" onClick={onDownload} type="button">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Download
          </button>
        </div>
      </div>

      {/* ── MAIN BODY ───────────────────────────────────────────────── */}
      <div className="fp-body">

        {/* ── PDF VIEWER (CENTER) ─────────────────────────────────── */}
        <div className="fp-pdf-area">

          {/* PDF toolbar */}
          <div className="fp-pdf-toolbar">
            <div className="fp-pdf-toolbar-left">
              <span className="fp-pdf-label">
                {pdfPages.length > 0 ? `${pdfPages.length} pages` : 'Loading…'}
              </span>
              {editMode && (
                <span className="fp-edit-active-badge">
                  <span className="fp-edit-dot" />
                  Edit mode — click blue fields
                </span>
              )}
            </div>
            {/* No inline-editable fields on this doc (e.g. EIN's SS-4, where
                overlayFields is []) — nothing to click, so don't show the
                affordance at all rather than an edit mode with no fields. */}
            {overlayFields.length > 0 && (
              <div className="fp-pdf-toolbar-right">
                {!editMode ? (
                  <button className="fp-btn-edit" onClick={() => setEditMode(true)} type="button">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Edit Fields
                  </button>
                ) : (
                  <>
                    <button className="fp-btn-edit-exit" onClick={handleExitEditMode} type="button">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      Done Editing
                    </button>
                    {isDirty && (
                      <button
                        className="fp-btn-update"
                        onClick={handleRegenerate}
                        disabled={isRegenerating}
                        type="button"
                      >
                        {isRegenerating ? (
                          <><div className="fp-spinner" /> Updating…</>
                        ) : (
                          <>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                              <path d="M23 4v6h-6M1 20v-6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"
                                stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            Update Form
                          </>
                        )}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Pages */}
          <div className="fp-pdf-scroll">
            {pdfPages.length === 0 ? (
              <div className="fp-pdf-loading">
                <div className="fp-spinner-lg" />
                <p>Rendering document…</p>
              </div>
            ) : (
              pdfPages.map((page, i) => (
                <div key={i} className="fp-pdf-page">
                  <div className="fp-page-label">Page {i + 1} of {pdfPages.length}</div>
                  <div className="fp-page-shadow">
                    <PDFPage
                      pdfPage={page}
                      pageNum={i + 1}
                      scale={SCALE}
                      overlayFields={overlayFields}
                      localData={localData}
                      onFieldChange={handleFieldChange}
                      editMode={editMode}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── RIGHT PANEL (ACTION SIDEBAR) ────────────────────────── */}
        <div className="fp-panel">

          {/* Filing summary */}
          <div className="fp-panel-section">
            <div className="fp-panel-section-title">Filing Summary</div>
            <div className="fp-summary-grid">
              {summaryFields.map(({ label, key }) => (
                <div className="fp-summary-item" key={key}>
                  <span className="fp-summary-label">{label}</span>
                  <span className="fp-summary-value">{filingData?.[key] || '—'}</span>
                </div>
              ))}
            </div>
            {showTxSummary && (
              <div className="fp-tx-chips">
                <div className="fp-tx-chip fp-tx-chip--out">
                  <span className="fp-tx-chip-label">Paid to LLC</span>
                  <span className="fp-tx-chip-val">${fmt(totalPaid)}</span>
                </div>
                <div className="fp-tx-chip fp-tx-chip--in">
                  <span className="fp-tx-chip-label">From LLC</span>
                  <span className="fp-tx-chip-val">${fmt(totalReceived)}</span>
                </div>
              </div>
            )}
          </div>

          <div className="fp-panel-divider" />

          {/* Signature section */}
          <div className="fp-panel-section">
            <div className="fp-panel-section-title">
              Signature
              {!signatureData && <span className="fp-required-badge">Required</span>}
              {signatureData  && <span className="fp-done-badge">✓ Applied</span>}
            </div>

            {!showSigPad ? (
              signatureData ? (
                <div className="fp-sig-preview-wrap">
                  <div className="fp-sig-preview">
                    <img src={signatureData} alt="Your signature" className="fp-sig-img" />
                  </div>
                  <div className="fp-sig-meta">
                    <span className="fp-sig-meta-text">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                        <polyline points="20 6 9 17 4 12" stroke="#22c894" strokeWidth="2.5" strokeLinecap="round"/>
                      </svg>
                      Signature applied
                    </span>
                    <button
                      className="fp-sig-change-btn"
                      onClick={() => { setSignatureData(null); setConfirmed(false); }}
                      type="button"
                    >
                      Change
                    </button>
                  </div>
                </div>
              ) : (
                <div className="fp-sig-empty" onClick={() => setShowSigPad(true)}>
                  <div className="fp-sig-empty-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"
                        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <div className="fp-sig-empty-title">Click to add signature</div>
                    <div className="fp-sig-empty-sub">Draw or upload your signature</div>
                  </div>
                </div>
              )
            ) : (
              <SignaturePad
                onDone={handleSignatureDone}
                onCancel={() => setShowSigPad(false)}
              />
            )}

            {signatureData && !showSigPad && (
              <button
                className="fp-sig-redo-btn"
                onClick={() => setShowSigPad(true)}
                type="button"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Re-sign
              </button>
            )}
          </div>

          <div className="fp-panel-divider" />

          {/* Confirmation */}
          <div className="fp-panel-section">
            <div className="fp-panel-section-title">Confirmation</div>

            {(isDirty || editMode) ? (
              <div className="fp-confirm-blocked">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                {editMode ? 'Exit edit mode to confirm' : 'Update the form before confirming'}
              </div>
            ) : (
              <label className="fp-confirm-label">
                <div className={`fp-confirm-checkbox ${confirmed ? 'checked' : ''}`}
                  onClick={() => signatureData && setConfirmed(c => !c)}>
                  {confirmed && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                      <polyline points="20 6 9 17 4 12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
                <span className={`fp-confirm-text ${!signatureData ? 'disabled' : ''}`}>
                  {confirmText}
                </span>
              </label>
            )}
          </div>

          <div className="fp-panel-divider" />

          {/* Send button */}
          <div className="fp-panel-section">
            <button
              className="fp-send-btn"
              onClick={handleSend}
              disabled={!confirmed || !signatureData || isProcessing || isRegenerating || isDirty || editMode}
              type="button"
            >
              {isProcessing ? (
                <>
                  <div className="fp-spinner fp-spinner--white" />
                  {sendingLabel}
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <line x1="22" y1="2" x2="11" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <polygon points="22 2 15 22 11 13 2 9 22 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {sendLabel}
                </>
              )}
            </button>

            {processingError && (
              <div className="fp-error">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                {processingError}
              </div>
            )}

            <div className="fp-send-note">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              {footerNote}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}